import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import EmbeddedAdminLayout from "../components/EmbeddedAdminLayout";
import AdminLayout from '../components/AdminLayout';
import EditorialPageHeader from '../components/EditorialPageHeader';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import './operationalMigration.css';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Upload, Link2, CheckCircle, XCircle, Clock, FileSpreadsheet,
  Download, RefreshCw, ChevronRight, AlertCircle, Loader2,
  Database, Users, Calendar, Trophy, ClipboardList, Home
} from 'lucide-react';

const ImportHub = ({ embedded = false }) => {
  const PageLayout = embedded ? EmbeddedAdminLayout : AdminLayout;
  const { token } = useAuth();
  const [platforms, setPlatforms] = useState([]);
  const [connections, setConnections] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [activeTab, setActiveTab] = useState('platforms');
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [connectionForm, setConnectionForm] = useState({});
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [csvMappings, setCsvMappings] = useState({});
  const [csvImportType, setCsvImportType] = useState('players');
  const [importing, setImporting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoadError(false);
    try {
      const [connectionsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/import/connections`, { headers }),
        axios.get(`${API}/import/jobs`, { headers })
      ]);
      
      setPlatforms(connectionsRes.data.platforms || []);
      setConnections(connectionsRes.data.connections || []);
      setJobs(jobsRes.data.jobs || []);
    } catch (error) {
      setLoadError(true);
      console.error('Error fetching import data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleConnect = async (platformId) => {
    try {
      setImporting(true);
      let endpoint = '';
      let payload = {};

      switch (platformId) {
        case 'sportsengine':
          endpoint = '/import/connect/sportsengine';
          payload = { access_token: connectionForm.access_token };
          break;
        case 'leagueapps':
          endpoint = '/import/connect/leagueapps';
          payload = {
            public_api_key: connectionForm.public_api_key,
            site_id: connectionForm.site_id,
            private_api_key: connectionForm.private_api_key
          };
          break;
        case 'teamsnap':
          endpoint = '/import/connect/teamsnap';
          payload = { access_token: connectionForm.access_token };
          break;
        default:
          return;
      }

      await axios.post(`${API}${endpoint}`, payload, { headers });
      setConnectingPlatform(null);
      setConnectionForm({});
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to connect');
    } finally {
      setImporting(false);
    }
  };

  const handleDisconnect = async (source) => {
    if (!window.confirm(`Disconnect ${source}?`)) return;
    
    try {
      await axios.delete(`${API}/import/disconnect/${source}`, { headers });
      fetchData();
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setCsvFile(file);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/import/csv/preview`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      setCsvPreview(response.data);
      
      // Set initial mappings from suggestions
      const initialMappings = {};
      response.data.suggested_mappings.forEach(m => {
        if (m.suggested_target) {
          initialMappings[m.source_field] = m.suggested_target;
        }
      });
      setCsvMappings(initialMappings);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to preview CSV');
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;
    
    setImporting(true);
    
    const formData = new FormData();
    formData.append('file', csvFile);
    formData.append('import_type', csvImportType);
    formData.append('field_mappings', JSON.stringify(csvMappings));
    formData.append('skip_duplicates', 'true');
    
    try {
      const response = await axios.post(`${API}/import/csv/import`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      
      alert(`Import started! Job ID: ${response.data.job_id}`);
      setCsvPreview(null);
      setCsvFile(null);
      setCsvMappings({});
      setActiveTab('history');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const startPlatformImport = async (source, importType) => {
    try {
      setImporting(true);
      const response = await axios.post(`${API}/import/${source}/${importType}`, {}, { headers });
      alert(`Import started! Job ID: ${response.data.job_id}`);
      setActiveTab('history');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-slate-100 text-slate-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-emerald-100 text-emerald-700',
      partial: 'bg-amber-100 text-amber-700',
      failed: 'bg-red-100 text-red-700'
    };
    
    const icons = {
      pending: <Clock className="h-3 w-3" />,
      processing: <Loader2 className="h-3 w-3 animate-spin" />,
      completed: <CheckCircle className="h-3 w-3" />,
      partial: <AlertCircle className="h-3 w-3" />,
      failed: <XCircle className="h-3 w-3" />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status}
      </span>
    );
  };

  const getImportTypeIcon = (type) => {
    const icons = {
      teams: <Trophy className="h-4 w-4" />,
      players: <Users className="h-4 w-4" />,
      coaches: <Users className="h-4 w-4" />,
      households: <Home className="h-4 w-4" />,
      schedules: <Calendar className="h-4 w-4" />,
      programs: <ClipboardList className="h-4 w-4" />,
      registrations: <Database className="h-4 w-4" />
    };
    return icons[type] || <Database className="h-4 w-4" />;
  };

  if (loadError && !loading) {
    return (
      <PageLayout>
        <div className="operational-migration space-y-6">
          <EditorialPageHeader title="Import Hub" />
          <div role="alert" className="migration-panel">
            <p>Failed to load import data. Please try again.</p>
            <Button variant="outline" onClick={() => { setLoading(true); fetchData(); }}>Retry</Button>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout>
        <div role="status" className="operational-migration flex items-center justify-center gap-3 h-64">
          <span>Loading import data…</span>
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="operational-migration import-workspace space-y-6" data-testid="import-hub">
        <EditorialPageHeader
          eyebrow="Organization settings"
          title="Import Hub"
          description="Import data from other platforms or CSV files"
          headingId="import-hub-title"
          aside={<Button onClick={fetchData} variant="outline" size="sm"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList aria-label="Import views" className="migration-tabs">
            {[
              { id: 'platforms', label: 'Platforms', icon: Link2 },
              { id: 'csv', label: 'CSV Import', icon: FileSpreadsheet },
              { id: 'history', label: 'Import History', icon: Clock }
            ].map(tab => (
              <TabsTrigger key={tab.id} value={tab.id} className="gap-2">
                <tab.icon className="h-4 w-4" />
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={activeTab}>

        {/* Platforms Tab */}
        {activeTab === 'platforms' && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {platforms.length === 0 && (
              <div className="migration-panel md:col-span-2 lg:col-span-3">
                <h2 className="font-semibold">No import platforms available</h2>
                <p className="text-sm mt-2 mb-4">Refresh to check for available connections, or import your records from a CSV file.</p>
                <Button variant="outline" onClick={() => setActiveTab('csv')}>Import a CSV file</Button>
              </div>
            )}
            {platforms.map(platform => (
              <div
                key={platform.id}
                className="migration-panel"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {platform.logo ? (
                      <img src={platform.logo} alt={platform.name} className="h-10 w-10 rounded-lg" />
                    ) : (
                      <div className="h-10 w-10 bg-slate-100 rounded-lg flex items-center justify-center">
                        <FileSpreadsheet className="h-5 w-5 text-slate-500" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-slate-900">{platform.name}</h3>
                      {platform.connected && (
                        <span className="text-xs text-emerald-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Connected
                        </span>
                      )}
                    </div>
                  </div>
                  {platform.csv_only && (
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                      CSV Only
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-slate-600 mb-4">{platform.description}</p>
                
                <div className="flex flex-wrap gap-1 mb-4">
                  {platform.supported_imports.map(type => (
                    <span
                      key={type}
                      className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded capitalize"
                    >
                      {type}
                    </span>
                  ))}
                </div>
                
                {platform.id === 'csv' ? (
                  <Button
                    onClick={() => setActiveTab('csv')}
                    variant="outline"
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload CSV
                  </Button>
                ) : platform.connected ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      {(platform.id === 'sportsengine' ? platform.supported_imports : platform.supported_imports.slice(0, 2)).map(type => (
                        <Button
                          key={type}
                          onClick={() => startPlatformImport(platform.id, type)}
                          size="sm"
                          variant="outline"
                          disabled={importing || (platform.id === 'sportsengine' && type !== 'teams')}
                          className={platform.id === 'sportsengine' && type !== 'teams' ? 'import-unavailable' : undefined}
                          aria-label={platform.id === 'sportsengine' && type !== 'teams' ? `${type} — unavailable` : undefined}
                        >
                          {getImportTypeIcon(type)}
                          <span className="ml-1 capitalize">{type}{platform.id === 'sportsengine' && type !== 'teams' ? ' — unavailable' : ''}</span>
                        </Button>
                      ))}
                    </div>
                    {platform.id === 'sportsengine' && (
                      <p className="text-sm text-slate-600">Only Teams has an implemented SportsEngine import endpoint. Players, coaches, schedules and registrations are unavailable in this version.</p>
                    )}
                    <Button
                      onClick={() => handleDisconnect(platform.id)}
                      variant="ghost"
                      size="sm"
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Disconnect
                    </Button>
                  </div>
                ) : platform.csv_only ? (
                  <Button
                    onClick={() => setActiveTab('csv')}
                    variant="outline"
                    className="w-full"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import via CSV
                  </Button>
                ) : (
                  <Button
                    onClick={() => setConnectingPlatform(platform.id)}
                    className="w-full"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Connect
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* CSV Import Tab */}
        {activeTab === 'csv' && (
          <div className="migration-panel">
            {!csvPreview ? (
              <div className="text-center py-12">
                <FileSpreadsheet className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Upload CSV File</h3>
                <p className="text-slate-500 mb-6">
                  Import players, coaches, teams, or schedules from any CSV file
                </p>
                
                <div className="flex flex-col items-center gap-4">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCsvUpload}
                      className="sr-only peer"
                    />
                    <div className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-md font-medium flex items-center gap-2 transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2">
                      <Upload className="h-5 w-5" />
                      Choose CSV File
                    </div>
                  </label>
                  
                  <div className="text-sm text-slate-500">
                    Or download a template:
                    <div className="flex flex-wrap gap-2 mt-2 justify-center">
                      {['players', 'coaches', 'teams', 'households'].map(type => (
                        <a
                          key={type}
                          href={`${API}/import/templates/${type}/download`}
                          className="text-primary hover:underline capitalize"
                        >
                          {type}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{csvPreview.filename}</h3>
                    <p className="text-sm text-slate-500">{csvPreview.total_rows} rows found</p>
                  </div>
                  <Button
                    onClick={() => {
                      setCsvPreview(null);
                      setCsvFile(null);
                      setCsvMappings({});
                    }}
                    variant="outline"
                    size="sm"
                  >
                    Upload Different File
                  </Button>
                </div>

                {/* Import Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    What are you importing?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['players', 'coaches', 'teams', 'households'].map(type => (
                      <Button
                        key={type}
                        onClick={() => setCsvImportType(type)}
                        variant={csvImportType === type ? 'default' : 'outline'}
                        size="sm"
                      >
                        {getImportTypeIcon(type)}
                        <span className="ml-2 capitalize">{type}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Field Mapping */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Map Fields</h4>
                  <div className="migration-panel migration-table">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 font-medium text-slate-700">CSV Column</th>
                          <th className="text-left p-3 font-medium text-slate-700">Sample Data</th>
                          <th className="text-left p-3 font-medium text-slate-700">Maps To</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {csvPreview.suggested_mappings.map((mapping, idx) => (
                          <tr key={idx}>
                            <td className="p-3 font-medium">{mapping.source_field}</td>
                            <td className="p-3 text-slate-500 truncate max-w-[200px]">
                              {mapping.sample_values[0] || '-'}
                            </td>
                            <td className="p-3">
                              <select
                                aria-label={`Map ${mapping.source_field}`}
                                value={csvMappings[mapping.source_field] || ''}
                                onChange={(e) => setCsvMappings({
                                  ...csvMappings,
                                  [mapping.source_field]: e.target.value
                                })}
                                className="w-full border rounded px-2 py-1 text-sm"
                              >
                                <option value="">-- Skip --</option>
                                {csvPreview.available_targets[csvImportType]?.map(target => (
                                  <option key={target} value={target}>
                                    {target.replace(/_/g, ' ')}
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Import Button */}
                <div className="flex justify-end gap-3">
                  <Button
                    onClick={() => {
                      setCsvPreview(null);
                      setCsvFile(null);
                    }}
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCsvImport}
                    disabled={importing || Object.keys(csvMappings).length === 0}
                  >
                    {importing ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Import {csvPreview.total_rows} Records
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Import History Tab */}
        {activeTab === 'history' && (
          <div className="migration-panel migration-table">
            {jobs.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No Import History</h3>
                <p className="text-slate-500">Your import jobs will appear here</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-700">Source</th>
                    <th className="text-left p-4 font-medium text-slate-700">Type</th>
                    <th className="text-left p-4 font-medium text-slate-700">Status</th>
                    <th className="text-left p-4 font-medium text-slate-700">Progress</th>
                    <th className="text-left p-4 font-medium text-slate-700">Records</th>
                    <th className="text-left p-4 font-medium text-slate-700">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {jobs.map(job => (
                    <tr key={job.job_id} className="hover:bg-slate-50">
                      <td className="p-4">
                        <span className="font-medium capitalize">{job.source}</span>
                        {job.filename && (
                          <span className="block text-xs text-slate-500">{job.filename}</span>
                        )}
                      </td>
                      <td className="p-4">
                        <span className="flex items-center gap-2 capitalize">
                          {getImportTypeIcon(job.import_type)}
                          {job.import_type}
                        </span>
                      </td>
                      <td className="p-4">{getStatusBadge(job.status)}</td>
                      <td className="p-4">
                        <div className="w-24 bg-slate-200 rounded-full h-2">
                          <div
                            className="bg-primary h-2 rounded-full transition-all"
                            style={{ width: `${job.progress || 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500">{job.progress || 0}%</span>
                      </td>
                      <td className="p-4 text-sm">
                        <span className="text-emerald-600">{job.imported_records || 0}</span>
                        {' / '}
                        <span className="text-slate-500">{job.total_records || 0}</span>
                        {job.skipped_records > 0 && (
                          <span className="text-amber-600 ml-1">({job.skipped_records} skipped)</span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-slate-500">
                        {new Date(job.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

          </TabsContent>
        </Tabs>

        {/* Connection Modal */}
        {connectingPlatform && (
          <div className="sh-modal-overlay fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="sh-modal-surface bg-white rounded-xl p-6 max-w-md w-full mx-4">
              <h3 className="sh-modal-title text-lg font-semibold mb-4 capitalize">
                Connect {connectingPlatform}
              </h3>
              
              {connectingPlatform === 'sportsengine' && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Enter your SportsEngine API access token. You can get this from the
                    <a href="https://developer.sportsengine.com" target="_blank" rel="noopener noreferrer" className="text-primary ml-1">
                      SportsEngine Developer Portal
                    </a>
                  </p>
                  <Input
                    placeholder="Access Token"
                    value={connectionForm.access_token || ''}
                    onChange={(e) => setConnectionForm({ ...connectionForm, access_token: e.target.value })}
                  />
                </div>
              )}
              
              {connectingPlatform === 'leagueapps' && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Enter your LeagueApps API credentials from
                    <a href="https://manager.leagueapps.com" target="_blank" rel="noopener noreferrer" className="text-primary ml-1">
                      LeagueApps Manager → Integrations → API Settings
                    </a>
                  </p>
                  <Input
                    placeholder="Site ID"
                    value={connectionForm.site_id || ''}
                    onChange={(e) => setConnectionForm({ ...connectionForm, site_id: e.target.value })}
                  />
                  <Input
                    placeholder="Public API Key"
                    value={connectionForm.public_api_key || ''}
                    onChange={(e) => setConnectionForm({ ...connectionForm, public_api_key: e.target.value })}
                  />
                  <Input
                    placeholder="Private API Key (Optional)"
                    value={connectionForm.private_api_key || ''}
                    onChange={(e) => setConnectionForm({ ...connectionForm, private_api_key: e.target.value })}
                  />
                </div>
              )}
              
              {connectingPlatform === 'teamsnap' && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Enter your TeamSnap OAuth access token.
                  </p>
                  <Input
                    placeholder="Access Token"
                    value={connectionForm.access_token || ''}
                    onChange={(e) => setConnectionForm({ ...connectionForm, access_token: e.target.value })}
                  />
                </div>
              )}
              
              <div className="sh-modal-footer flex justify-end gap-3 mt-6">
                <Button
                  onClick={() => {
                    setConnectingPlatform(null);
                    setConnectionForm({});
                  }}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleConnect(connectingPlatform)}
                  disabled={importing}
                >
                  {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                  Connect
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default ImportHub;
